<template>
  <div class="relative min-h-screen w-full flex bg-gradient-to-br from-gray-100 to-blue-50">
    <div class="h-full container mx-auto">
      <div class="flex flex-col h-full w-full p-0 sm:p-8">
        <nav class="text-sm text-gray-600 mb-6 flex">
          <router-link to="/dashboard" class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
          <span class="mx-1">/</span>
          <span class="text-blue-900 font-semibold">Welcome quotes</span>
        </nav>

        <div class="header-section bg-white shadow-sm rounded-lg p-6 mb-6">
          <div class="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 class="text-2xl font-bold text-gray-900 mb-2">Welcome quotes</h1>
              <p class="text-gray-600">Manage inspirational quotes shown on the main dashboard.</p>
            </div>
            <base-button
              v-if="hasPermission('welcome-quotes-create')"
              type="button"
              @click="openCreate"
              class="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <font-awesome-icon icon="plus" class="mr-2" />
              Add quote
            </base-button>
          </div>
        </div>

        <div class="bg-white shadow-sm rounded-lg flex-auto flex flex-col">
          <div class="p-6 border-b border-gray-200">
            <h2 class="text-lg font-semibold text-gray-900">All quotes</h2>
          </div>

          <div class="flex-auto flex flex-col" v-if="loading">
            <table-shimmer
              :titleHeaders="['#', 'Quote', 'Author', 'Created by', 'Created', 'Actions']"
              :rows="perPage"
            />
          </div>

          <div
            v-else-if="!loading && quotesList.length === 0"
            class="flex-auto flex justify-center items-center py-12"
          >
            <div class="text-center max-w-md px-4">
              <h2 class="text-xl font-semibold text-gray-800 mb-2">No quotes yet</h2>
              <p class="text-gray-500 mb-4">Add a quote to show it on the dashboard welcome area.</p>
              <base-button
                v-if="hasPermission('welcome-quotes-create')"
                type="button"
                @click="openCreate"
                class="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <font-awesome-icon icon="plus" class="mr-2" />
                Add quote
              </base-button>
            </div>
          </div>

          <div v-else class="flex flex-col flex-auto">
            <div class="flex-auto flex flex-col overflow-x-auto">
              <div class="inline-block min-w-full py-2">
                <div class="overflow-x-auto">
                  <table class="min-w-full text-left text-sm font-light">
                    <thead class="border-b font-medium dark:border-neutral-500">
                      <tr>
                        <th scope="col" class="px-6 py-2 text-gray-400 w-8">#</th>
                        <th scope="col" class="px-6 py-2 text-gray-400">Quote</th>
                        <th scope="col" class="px-6 py-2 text-gray-400">Author</th>
                        <th scope="col" class="px-6 py-2 text-gray-400">Created by</th>
                        <th scope="col" class="px-6 py-2 text-gray-400">Created</th>
                        <th scope="col" class="px-6 py-2 text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(row, index) in quotesList"
                        :key="row.id"
                        class="border-b border-dashed dark:border-neutral-500 hover:bg-gray-50"
                      >
                        <td class="whitespace-nowrap px-6 py-2 font-medium">
                          {{ rowNumber(index) }}
                        </td>
                        <td class="px-6 py-2 text-gray-800 max-w-md">
                          <div class="truncate">{{ row.quote }}</div>
                        </td>
                        <td class="px-6 py-2 text-gray-900">{{ row.author }}</td>
                        <td class="px-6 py-2 text-gray-900">
                          {{ row.creator && row.creator.name ? row.creator.name : '—' }}
                        </td>
                        <td class="px-6 py-2 text-gray-900">{{ formatDate(row.created_at) }}</td>
                        <td class="px-6 py-2">
                          <div class="flex">
                            <button
                              v-if="hasPermission('welcome-quotes-update')"
                              type="button"
                              title="Edit"
                              class="bg-gray-100 hover:bg-indigo-50 w-8 h-8 flex justify-center items-center rounded-lg mr-2"
                              @click="openEdit(row)"
                            >
                              <font-awesome-icon icon="pen" class="w-3 h-3 text-gray-700" />
                            </button>
                            <button
                              v-if="hasPermission('welcome-quotes-delete')"
                              type="button"
                              title="Delete"
                              class="bg-gray-100 hover:bg-red-50 w-8 h-8 flex justify-center items-center rounded-lg"
                              @click="confirmDelete(row)"
                            >
                              <font-awesome-icon icon="trash" class="w-3 h-3 text-gray-700" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div class="mt-4 px-6 pb-6" v-if="paginatorInfoWithItems && paginatorInfoWithItems.lastPage > 1">
              <pagination :paginatorInfo="paginatorInfoWithItems" :setPage="setPage"></pagination>
            </div>
          </div>
        </div>
      </div>
    </div>

    <transition name="fade" mode="out-in">
      <div
        v-if="showFormModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4"
        @click.self="closeForm"
      >
        <div class="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-screen overflow-y-auto p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">
            {{ editingId ? 'Edit quote' : 'New quote' }}
          </h3>
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Quote</label>
            <textarea
              v-model="form.quote"
              rows="5"
              class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter the quote text"
            ></textarea>
          </div>
          <div class="mb-6">
            <label class="block text-sm font-medium text-gray-700 mb-1">Author</label>
            <input
              v-model="form.author"
              type="text"
              class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Author name"
            />
          </div>
          <div class="flex justify-end gap-2">
            <button
              type="button"
              class="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              @click="closeForm"
            >
              Cancel
            </button>
            <base-button
              type="button"
              :disabled="saving"
              @click="saveForm"
              class="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {{ saving ? 'Saving…' : 'Save' }}
            </base-button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import gql from 'graphql-tag';
import Pagination from '../../components/shared/pagination.vue';
import TableShimmer from '../../components/shared/TableShimmer.vue';

const LIST_QUERY = gql`
  query WelcomeQuotesList($first: Int!, $page: Int!) {
    welcomeQuotesList(first: $first, page: $page) {
      error
      error_code
      message
      data {
        id
        quote
        author
        created_at
        creator {
          id
          name
        }
      }
      paginatorInfo {
        currentPage
        lastPage
        perPage
        total
        hasMorePages
        firstItem
        lastItem
      }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation CreateWelcomeQuote($input: WelcomeQuoteInput!) {
    createWelcomeQuote(input: $input) {
      error
      error_code
      message
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation UpdateWelcomeQuote($id: ID!, $input: WelcomeQuoteInput!) {
    updateWelcomeQuote(id: $id, input: $input) {
      error
      error_code
      message
    }
  }
`;

const DELETE_MUTATION = gql`
  mutation DeleteWelcomeQuote($id: ID!) {
    deleteWelcomeQuote(id: $id) {
      error
      error_code
      message
    }
  }
`;

export default {
  name: 'welcome-quotes-index',
  components: {
    Pagination,
    TableShimmer,
  },
  data() {
    return {
      quotesList: [],
      paginatorInfo: null,
      loading: false,
      perPage: 10,
      page: 1,
      showFormModal: false,
      editingId: null,
      saving: false,
      form: {
        quote: '',
        author: '',
      },
    };
  },
  computed: {
    role() {
      return this.$store.getters.role;
    },
    paginatorInfoWithItems() {
      if (!this.paginatorInfo) {
        return null;
      }
      var pi = this.paginatorInfo;
      var firstItem =
        pi.firstItem ||
        (pi.currentPage - 1) * pi.perPage + 1;
      var lastItem =
        pi.lastItem ||
        Math.min(pi.currentPage * pi.perPage, pi.total);
      return {
        currentPage: pi.currentPage,
        lastPage: pi.lastPage,
        perPage: pi.perPage,
        total: pi.total,
        hasMorePages: pi.hasMorePages,
        firstItem: firstItem,
        lastItem: lastItem,
      };
    },
  },
  created() {
    this.loadList();
  },
  methods: {
    /**
     * Same toast pattern as current-partners and survey (mixin + top-end + progress bar).
     */
    toastAlert(options) {
      if (!this.$swal) {
        return Promise.resolve({});
      }
      var merged = Object.assign(
        {
          timer: 2000,
        },
        options
      );
      if (
        (merged.icon === 'error' || merged.icon === 'warning') &&
        (options.timer === undefined || options.timer === null)
      ) {
        merged.timer = 3500;
      }
      var self = this;
      var Toast = this.$swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timerProgressBar: true,
        didOpen: function (toast) {
          toast.addEventListener('mouseenter', self.$swal.stopTimer);
          toast.addEventListener('mouseleave', self.$swal.resumeTimer);
        },
      });
      return Toast.fire(merged);
    },
    hasPermission(crudName) {
      if (this.role == 'superadmin') {
        return true;
      }
      var permissions = this.$store.getters.permissions;
      var permissionsArray = permissions.split(',');
      if (permissionsArray.includes(crudName)) {
        return true;
      }
      return false;
    },
    rowNumber(index) {
      if (this.paginatorInfoWithItems && this.paginatorInfoWithItems.firstItem) {
        return this.paginatorInfoWithItems.firstItem + index;
      }
      return index + 1;
    },
    formatDate(value) {
      if (!value) {
        return '—';
      }
      var d = new Date(value);
      if (isNaN(d.getTime())) {
        return String(value);
      }
      return d.toLocaleString();
    },
    async loadList() {
      this.loading = true;
      try {
        var result = await this.$apollo.query({
          query: LIST_QUERY,
          variables: {
            first: this.perPage,
            page: this.page,
          },
          fetchPolicy: 'network-only',
        });
        var data = result && result.data ? result.data : null;
        var payload = data && data.welcomeQuotesList ? data.welcomeQuotesList : null;
        if (!payload) {
          throw new Error('No response');
        }
        if (payload.error) {
          this.toastAlert({
            icon: 'error',
            title: 'Unable to load',
            text: payload.message,
          });
          this.quotesList = [];
          this.paginatorInfo = null;
        } else {
          this.quotesList = payload.data || [];
          this.paginatorInfo = payload.paginatorInfo;
        }
      } catch (e) {
        this.toastAlert({
          icon: 'error',
          title: 'Failed',
          text: 'Could not load welcome quotes.',
        });
        this.quotesList = [];
        this.paginatorInfo = null;
      } finally {
        this.loading = false;
      }
    },
    setPage(p) {
      this.page = p;
      this.loadList();
    },
    openCreate() {
      this.editingId = null;
      this.form = { quote: '', author: '' };
      this.showFormModal = true;
    },
    openEdit(row) {
      this.editingId = row.id;
      this.form = { quote: row.quote, author: row.author };
      this.showFormModal = true;
    },
    closeForm() {
      this.showFormModal = false;
      this.editingId = null;
    },
    async saveForm() {
      var q = (this.form.quote || '').trim();
      var a = (this.form.author || '').trim();
      if (!q || !a) {
        this.toastAlert({ icon: 'warning', title: 'Missing fields', text: 'Quote and author are required.' });
        return;
      }
      this.saving = true;
      try {
        if (this.editingId) {
          var up = await this.$apollo.mutate({
            mutation: UPDATE_MUTATION,
            variables: { id: String(this.editingId), input: { quote: q, author: a } },
          });
          var ur = up && up.data ? up.data.updateWelcomeQuote : null;
          if (ur && ur.error) {
            this.toastAlert({ icon: 'error', title: 'Error', text: ur.message });
          } else {
            this.toastAlert({ icon: 'success', title: 'Saved', timer: 2000 });
            this.closeForm();
            this.loadList();
          }
        } else {
          var cr = await this.$apollo.mutate({
            mutation: CREATE_MUTATION,
            variables: { input: { quote: q, author: a } },
          });
          var crd = cr && cr.data ? cr.data.createWelcomeQuote : null;
          if (crd && crd.error) {
            this.toastAlert({ icon: 'error', title: 'Error', text: crd.message });
          } else {
            this.toastAlert({ icon: 'success', title: 'Created', timer: 2000 });
            this.closeForm();
            this.page = 1;
            this.loadList();
          }
        }
      } catch (err) {
        this.toastAlert({ icon: 'error', title: 'Failed', text: 'Request failed.' });
      } finally {
        this.saving = false;
      }
    },
    async confirmDelete(row) {
      if (!this.$swal) {
        return;
      }
      var res = await this.$swal.fire({
        title: 'Delete this quote?',
        text: 'This cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete',
      });
      if (!res || !res.isConfirmed) {
        return;
      }
      try {
        var del = await this.$apollo.mutate({
          mutation: DELETE_MUTATION,
          variables: { id: String(row.id) },
        });
        var dr = del && del.data ? del.data.deleteWelcomeQuote : null;
        if (dr && dr.error) {
          this.toastAlert({ icon: 'error', title: 'Error', text: dr.message });
        } else {
          this.toastAlert({ icon: 'success', title: 'Deleted', timer: 2000 });
          this.loadList();
        }
      } catch (err) {
        this.toastAlert({ icon: 'error', title: 'Failed', text: 'Could not delete.' });
      }
    },
  },
};
</script>
